import React, { useEffect, useRef, useState } from "react";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";

const MASTER_PLAN_URL =
  "https://urbangis.mp.gov.in/server/rest/services/temp_n/master_plan/MapServer";
const PROPERTY_URL =
  "https://urbangis.mp.gov.in/server/rest/services/Hosted/property_info_209/FeatureServer/1";
const ADMIN_LAYER_URL =
  "https://urbangis.mp.gov.in/server/rest/services/temp_n/urbangis_temp/MapServer";

const pageSize = 100;

export default function PropertyExplorer() {
  const mapRef = useRef(null);
  const viewRef = useRef(null);
  const masterSubRef = useRef(null);
  const propertyLayerRef = useRef(null);
  const propertyLayerViewRef = useRef(null);

  const [towns, setTowns] = useState([]);
  const [townToUlb, setTownToUlb] = useState({});
  const [landUses, setLandUses] = useState([]);
  const [selectedTown, setSelectedTown] = useState("");
  const [selectedLandUse, setSelectedLandUse] = useState("All");
  const [selectedType, setSelectedType] = useState("");
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(null);
  const [filteredCount, setFilteredCount] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [tableFields, setTableFields] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, totalPages: 0 });
  const unionGeomRef = useRef(null);
  

  useEffect(() => {
    // Initialize map and layers
    const map = new Map({ basemap: "topo-vector" });
    const view = new MapView({
      container: mapRef.current,
      map,
      center: [77.4, 23.25],
      zoom: 6,
    });
    viewRef.current = view;

    const masterMIL = new MapImageLayer({ url: MASTER_PLAN_URL });
    map.add(masterMIL);

    masterMIL.when(() => {
      const masterSub = masterMIL.findSublayerById(3);
      if (masterSub) {
        masterSub.definitionExpression = "1=0";
        masterSubRef.current = masterSub;
      }
    });

    const propertyLayer = new FeatureLayer({
      url: PROPERTY_URL,
      outFields: ["*"],
      visible: true,
      definitionExpression: "1=0",
    });
    map.add(propertyLayer);
    propertyLayerRef.current = propertyLayer;

    view.whenLayerView(propertyLayer).then((lv) => {
      propertyLayerViewRef.current = lv;
    });

    const adminLayer = new MapImageLayer({
      url: ADMIN_LAYER_URL,
      title: "Administrative Boundaries",
      sublayers: [{ id: 1, visible: true }, { id: 3, visible: true }],
    });
    map.add(adminLayer);

    populateTowns();

    return () => {
      if (view) {
        view.container = null;
      }
    };
  }, []);

  async function populateTowns() {
    const fl = new FeatureLayer({ url: MASTER_PLAN_URL + "/3" });
    const q = fl.createQuery();
    q.where = "1=1";
    q.outFields = ["town_name", "ulb_cd"];
    const res = await fl.queryFeatures(q);

    const seen = new Set();
    const townsList = [];
    const townUlbMap = {};

    res.features.forEach((f) => {
      const town = f.attributes.town_name;
      const ulb = f.attributes.ulb_cd;
      if (town && ulb && !seen.has(town)) {
        townsList.push(town);
        townUlbMap[town] = ulb;
        seen.add(town);
      }
    });

    setTowns(townsList);
    setTownToUlb(townUlbMap);
  }

  async function populateLandUseByTown(town) {
    setLandUses([]);
    if (!town) return;

    const fl = new FeatureLayer({ url: MASTER_PLAN_URL + "/3" });
    const q = fl.createQuery();
    q.where = `town_name='${town}'`;
    q.outFields = ["land_use"];
    const res = await fl.queryFeatures(q);

    const seen = new Set();
    const landUseList = [];
    res.features.forEach((f) => {
      const val = f.attributes.land_use;
      if (val && !seen.has(val)) {
        landUseList.push(val);
        seen.add(val);
      }
    });

    setLandUses(landUseList);
  }

  useEffect(() => {
    if (!selectedTown) {
      if (masterSubRef.current) masterSubRef.current.definitionExpression = "1=0";
      if (propertyLayerRef.current) propertyLayerRef.current.definitionExpression = "1=0";
      setLandUses([]);
      setSelectedLandUse("");
      setTotalCount(null);
      setFilteredCount(null);
      setTableData([]);
      setPagination({ page: 0, totalPages: 0 });
      unionGeomRef.current = null;
      return;
    }

    populateLandUseByTown(selectedTown);

    if (masterSubRef.current) masterSubRef.current.definitionExpression = `town_name='${selectedTown}'`;

    (async () => {
      const fl = new FeatureLayer({ url: MASTER_PLAN_URL + "/3" });
      const q = fl.createQuery();
      q.where = `town_name='${selectedTown}'`;
      const result = await fl.queryExtent(q);
      if (result.extent) {
        viewRef.current.goTo(result.extent.expand(1.2));
      }
    })();

    if (propertyLayerRef.current) {
      const ulb = townToUlb[selectedTown];
      propertyLayerRef.current.definitionExpression = `ulb_cd='${ulb}'`;
    }
  }, [selectedTown, townToUlb]);

  useEffect(() => {
    if (!selectedTown) return;
    let expr = `town_name='${selectedTown}'`;
    if (selectedLandUse) expr += ` AND land_use='${selectedLandUse}'`;
    if (masterSubRef.current) masterSubRef.current.definitionExpression = expr;
  }, [selectedLandUse, selectedTown]);

  useEffect(() => {
    if (!selectedTown) return;
    const ulb = townToUlb[selectedTown];
    let expr = `ulb_cd='${ulb}'`;
    if (selectedType) expr += ` AND landusetype='${selectedType}'`;
    if (propertyLayerRef.current) {
      propertyLayerRef.current.definitionExpression = expr;
    }
    if (propertyLayerViewRef.current && unionGeomRef.current) {
      propertyLayerViewRef.current.filter = { geometry: unionGeomRef.current, where: expr };
    }
  }, [selectedType, selectedTown, townToUlb]);

  async function runIntersect() {
    setLoading(true);
    if (!selectedTown) {
      setLoading(false);
      return;
    }

    const ulb = townToUlb[selectedTown];
    const fl = new FeatureLayer({ url: MASTER_PLAN_URL + "/3" });
    const q = fl.createQuery();
    q.where = `town_name='${selectedTown}'${selectedLandUse ? ` AND land_use='${selectedLandUse}'` : ""}`;
    q.returnGeometry = true;

    const mpFS = await fl.queryFeatures(q);
    if (mpFS.features.length === 0) {
      setTotalCount(null);
      setFilteredCount(null);
      setTableData([]);
      setPagination({ page: 0, totalPages: 0 });
      setLoading(false);
      return;
    }

    const unionGeom =
      mpFS.features.length > 1
        ? geometryEngine.union(mpFS.features.map((f) => f.geometry))
        : mpFS.features[0].geometry;
    unionGeomRef.current = unionGeom;

    let mpWhere = `town_name='${selectedTown}'`;
    if (selectedLandUse) mpWhere += ` AND land_use='${selectedLandUse}'`;
    if (masterSubRef.current) masterSubRef.current.definitionExpression = mpWhere;

    const totalQuery = propertyLayerRef.current.createQuery();
    totalQuery.geometry = unionGeom;
    totalQuery.spatialRelationship = "intersects";
    totalQuery.where = `ulb_cd='${ulb}'`;
    const totalCountResult = await propertyLayerRef.current.queryFeatureCount(totalQuery);

    let propWhere = `ulb_cd='${ulb}'`;
    if (selectedType) propWhere += ` AND landusetype='${selectedType}'`;

    if (propertyLayerRef.current) {
      propertyLayerRef.current.definitionExpression = propWhere;
    }
    if (propertyLayerViewRef.current) {
      propertyLayerViewRef.current.filter = { geometry: unionGeom, where: propWhere };
    }

    const filteredQuery = propertyLayerRef.current.createQuery();
    filteredQuery.geometry = unionGeom;
    filteredQuery.spatialRelationship = "intersects";
    filteredQuery.where = propWhere;
    const filteredCountResult = await propertyLayerRef.current.queryFeatureCount(filteredQuery);

    setTotalCount(totalCountResult);
    setFilteredCount(filteredCountResult);

    renderTablePaged(propWhere, unionGeom, 0, filteredCountResult);

    viewRef.current.goTo(unionGeom.extent.expand(1.2));
    setLoading(false);
  }

  async function renderTablePaged(where, geom, page, totalCountResult) {
    const query = propertyLayerRef.current.createQuery();
    query.where = where;
    query.geometry = geom;
    query.spatialRelationship = "intersects";
    query.outFields = ["*"];
    query.returnGeometry = false;
    query.start = page * pageSize;
    query.num = pageSize;

    const res = await propertyLayerRef.current.queryFeatures(query);
    const features = res.features;

    if (features.length === 0) {
      setTableData([]);
      setTableFields([]);
      setPagination({ page: 0, totalPages: 0 });
      return;
    }

    const fields = Object.keys(features[0].attributes);
    const rows = features.map((f) => f.attributes);

    setTableFields(fields);
    setTableData(rows);

    const totalPages = Math.ceil(totalCountResult / pageSize);
    setPagination({ page, totalPages, where, geom, totalCountResult });
  }

  function onPageChange(newPage) {
    renderTablePaged(pagination.where, pagination.geom, newPage, pagination.totalCountResult);
  }

  function reset() {
    if (masterSubRef.current) masterSubRef.current.definitionExpression = "1=0";
    if (propertyLayerRef.current) propertyLayerRef.current.definitionExpression = "1=0";
    setSelectedTown("");
    setSelectedLandUse("");
    setSelectedType("");
    setLandUses([]);
    setTotalCount(null);
    setFilteredCount(null);
    setTableData([]);
    setTableFields([]);
    setPagination({ page: 0, totalPages: 0 });
    unionGeomRef.current = null;
  }


  useEffect(() => {
  if (selectedLandUse && !loading) {
    runIntersect();
  }
}, [selectedLandUse,selectedType]);

  return (
    <div
      style={{
        height: "100vh",
        display: "grid",
        gridTemplateColumns: "280px 1fr",
        gridTemplateRows: "1fr auto",
      }}
    >
      {/* Sidebar - vertical right-aligned */}
      <aside
        style={{
          gridColumn: 1,
          gridRow: "1 / span 2",
          borderRight: "3px solid #006837",
          display: "flex",
          flexDirection: "column",
          background: "#f5f9f8",
          padding: 15,
          fontSize: 12,
          color: "#0c112b",
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: 12, fontSize: 18, color: "#005a8d" }}>
          Property Explorer
        </h2>

        <label style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: "bold" }}>Town:</div>
          <select
            value={selectedTown}
            onChange={(e) => setSelectedTown(e.target.value)}
            style={{ width: "100%", padding: 6, fontSize: 12 }}
          >
            <option value="">-- Select --</option>
            {towns.map((town) => (
              <option key={town} value={town}>
                {town}
              </option>
            ))}
          </select>
        </label>

        <label style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: "bold" }}>Master Plan Landuse:</div>
          <select
            value={selectedLandUse}
            onChange={(e) => setSelectedLandUse(e.target.value)}
            style={{ width: "100%", padding: 6, fontSize: 12 }}
          >
            <option value="">-- Select --</option>
            {landUses.map((lu) => (
              <option key={lu} value={lu}>
                {lu}
              </option>
            ))}
          </select>
        </label>

        <label style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: "bold" }}>Property Use:</div>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            style={{ width: "100%", padding: 6, fontSize: 12 }}
          >
            <option value="">All</option>
            <option value="Residential">Residential</option>
            <option value="Commercial">Commercial</option>
          
            {/* Add more options if needed */}
          </select>
        </label>

        <button
          disabled={loading}
          onClick={runIntersect}
          style={{
            backgroundColor: "#006837",
            color: "white",
            border: "none",
            padding: "10px",
            cursor: loading ? "not-allowed" : "pointer",
            marginBottom: 10,
            fontWeight: "bold",
          }}
        >
          {loading ? "Loading..." : "Filter Data"}
        </button>

        <button
          onClick={reset}
          style={{
            backgroundColor: "#f44336",
            color: "white",
            border: "none",
            padding: "10px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          Reset
        </button>

        {(totalCount !== null || filteredCount !== null) && (
          <div style={{ marginTop: 15, fontSize: 12, fontWeight: "bold" }}>
            <div>Total Properties in Town: {totalCount ?? "-"}</div>
            <div>Filtered Properties: {filteredCount ?? "-"}</div>
          </div>
        )}
      </aside>

      {/* Map */}
      <div ref={mapRef} style={{ width: "70%", height: "70%" }}></div>

      {/* Table */}
      <div
        style={{
          gridColumn: "2 / 3",
          gridRow: 2,
          overflow: "auto",
          borderTop: "1px solid #ccc",
          backgroundColor: "white",
          maxHeight: "300px",
        }}
      >
        {tableData.length > 0 ? (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                {tableFields.map((field) => (
                  <th
                    key={field}
                    style={{
                      border: "1px solid #ddd",
                      padding: "6px",
                      backgroundColor: "#eee",
                      fontWeight: "bold",
                      textAlign: "left",
                    }}
                  >
                    {field}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #ddd" }}>
                  {tableFields.map((field) => (
                    <td key={field} style={{ padding: "6px" }}>
                      {row[field]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: 15, fontSize: 14, fontStyle: "italic" }}>No data</div>
        )}

        {pagination.totalPages > 1 && (
          <div
            style={{
              marginTop: 8,
              padding: 10,
              display: "flex",
              justifyContent: "center",
              gap: 10,
            }}
          >
            <button
              onClick={() => onPageChange(Math.max(pagination.page - 1, 0))}
              disabled={pagination.page === 0}
            >
              Prev
            </button>
            <span>
              Page {pagination.page + 1} of {pagination.totalPages}
            </span>
            <button
              onClick={() => onPageChange(Math.min(pagination.page + 1, pagination.totalPages - 1))}
              disabled={pagination.page === pagination.totalPages - 1}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
